import type { ThemeConfig } from 'antd';
import { theme as antdTheme } from 'antd';
import { COLORS, SPACING, BORDER_RADIUS, TYPOGRAPHY } from './constants';

export type ThemeMode = 'light' | 'dark';
export interface ThemeOverrides {
  tokens?: Partial<ThemeConfig['token']>;
  components?: Partial<ThemeConfig['components']>;
}

const COMPACT_ALGORITHMS = [antdTheme.compactAlgorithm];

const getAlgorithms = (mode: ThemeMode): ThemeConfig['algorithm'] => [
  mode === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
  ...COMPACT_ALGORITHMS,
];

const baseTokens: ThemeConfig['token'] = {
  colorPrimary: COLORS.primary,
  borderRadius: BORDER_RADIUS.base,
  fontFamily: TYPOGRAPHY.fontFamily.base,
  fontSize: TYPOGRAPHY.fontSize.base,
  controlHeight: 28,
  controlHeightSM: 22,
  controlHeightLG: 36,
  lineHeight: 1.4,
  lineHeightHeading1: 1.2,
  lineHeightHeading2: 1.3,
  lineHeightHeading3: 1.3,
  lineHeightHeading4: 1.35,
  lineHeightHeading5: 1.4,
  padding: SPACING.md,
  paddingSM: SPACING.sm,
  paddingXS: SPACING.xs,
  paddingLG: SPACING.lg,
  margin: SPACING.md,
  marginSM: SPACING.sm,
  marginXS: SPACING.xs,
  marginLG: SPACING.lg,
  sizeStep: 3,
  sizeUnit: 3,
};

const buildComponentTokens = (mode: ThemeMode): ThemeConfig['components'] => ({
  Table: {
    cellPaddingBlock: 6,
    cellPaddingInline: 8,
    headerBg: 'transparent',
  },
  Card: {
    paddingLG: SPACING.lg,
  },
  Button: {
    paddingBlock: 2,
    paddingInline: 12,
    paddingBlockSM: 0,
    paddingInlineSM: 8,
  },
  Input: {
    paddingBlock: 2,
    paddingInline: 8,
  },
  Select: {
    controlHeight: 28,
    controlHeightSM: 22,
  },
  Form: {
    itemMarginBottom: 12,
    verticalLabelPadding: 2,
  },
  Modal: {
    contentBg: mode === 'dark' ? COLORS.gray[900] : '#ffffff',
    headerBg: mode === 'dark' ? COLORS.gray[900] : '#ffffff',
  },
});

const mergeComponentTokens = (
  base: ThemeConfig['components'] = {},
  overrides: Partial<ThemeConfig['components']> = {},
): ThemeConfig['components'] => {
  const keys = new Set([...Object.keys(base), ...Object.keys(overrides)]);
  const result: Record<string, unknown> = {};

  keys.forEach((key) => {
    const baseValue = (base as Record<string, unknown>)[key] ?? {};
    const overrideValue = (overrides as Record<string, unknown>)[key] ?? {};
    result[key] = { ...(baseValue as object), ...(overrideValue as object) };
  });

  return result as ThemeConfig['components'];
};

export const buildThemeConfig = (mode: ThemeMode, overrides?: ThemeOverrides): ThemeConfig => {
  const tokens = { ...baseTokens, ...(overrides?.tokens ?? {}) };
  const components = mergeComponentTokens(buildComponentTokens(mode), overrides?.components);

  return {
    algorithm: getAlgorithms(mode),
    token: tokens,
    components,
  };
};

